import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardMenuList, MenuItem } from "UIPalette/CardMenuList/CardMenuList";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useAcceptUserTermsFunctionRequest, useGetUserTermsStatusFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { TermKeys } from "core/enums/enums";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { ReactComponent as MindCalmingEmoji } from "shared/assets/Icons/emoji-mind-calming.svg";
import { ReactComponent as RelievedFaceEmoji } from "shared/assets/Icons/emoji-relieved-face.svg";
import { ReactComponent as BlushingFaceEmoji } from "shared/assets/Icons/emoji-blushing-face.svg";
import { COACHMARK_DELAY_MS, NotSureRoutingOption, SELECTION_DELAY_MS } from "../../enums/supportMoments/supportMoments";
import { NotSureRoutingCoachmark } from "../Coachmarks/NotSureRoutingCoachMark/NotSureRoutingCoachmark";

const ROUTING_OPTIONS: { option: NotSureRoutingOption; Emoji: MenuItem["emoji"] }[] = [
  { option: "calmMyMind", Emoji: MindCalmingEmoji },
  { option: "restMyBody", Emoji: RelievedFaceEmoji },
  { option: "noticeSomethingPositive", Emoji: BlushingFaceEmoji },
];

const COACHMARK_ITEM_INDEX = 0;

type NotSureRoutingOptionsProps = {
  onSelect: (option: NotSureRoutingOption) => void;
};

export const NotSureRoutingOptions = ({ onSelect }: NotSureRoutingOptionsProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments.notSureRouting" });
  const [showCoachmark, setShowCoachmark] = useState(false);
  const xClientCode = ClientInfoState.getCode();

  const {
    data: coachmarkStatus,
    isLoading: isLoadingCoachmarkStatus,
    mutate: mutateCoachmarkStatus,
  } = useGetUserTermsStatusFunctionRequest({ xClientCode, key: TermKeys.SupportMomentsTutorial });
  const { trigger: triggerAcceptCoachmark } = useAcceptUserTermsFunctionRequest({ xClientCode });

  useEffect(() => {
    if (isLoadingCoachmarkStatus || coachmarkStatus?.accepted) {
      return;
    }

    const timeout = setTimeout(() => {
      trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_VIEWED, { step: "2" });
      setShowCoachmark(true);
    }, COACHMARK_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isLoadingCoachmarkStatus, coachmarkStatus?.accepted]);

  const handleCoachmarkSkip = async (dismiss: () => void) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_SKIP, { step: "2" });
    setShowCoachmark(false);
    dismiss();

    await triggerAcceptCoachmark({ query: { key: TermKeys.SupportMomentsTutorial } });
    mutateCoachmarkStatus();
  };

  const handleCoachmarkNext = (dismiss: () => void) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_NEXT, { step: "2" });
    setShowCoachmark(false);
    dismiss();
  };

  const handleSelect = (option: NotSureRoutingOption) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_NOT_SURE_OPTION_SELECTED, { value: option });
    onSelect(option);
  };

  const menuItems: MenuItem[] = ROUTING_OPTIONS.map(({ option, Emoji }) => ({
    emoji: Emoji,
    title: t(`options.${option}`),
    theme: "green",
    onClick: () => handleSelect(option),
  }));

  return (
    <div className="space-y-6">
      <p className="text-center">{t("heading")}</p>
      <CardMenuList
        menuItems={menuItems}
        timeoutDuration={SELECTION_DELAY_MS}
        coachmark={{
          itemIndex: COACHMARK_ITEM_INDEX,
          initialOpenState: showCoachmark,
          content: ({ dismiss }) => (
            <NotSureRoutingCoachmark
              step={2}
              totalSteps={3}
              onSkip={() => handleCoachmarkSkip(dismiss)}
              onNext={() => handleCoachmarkNext(dismiss)}
            />
          ),
        }}
      />
    </div>
  );
};
